import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/modal/Modal";
import WelcomeCard from "../../components/WelcomeCard";
import Eye from "../../images/svgs/Eye";
import Sidebar from "../../components/Sidebar";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";
import { toast } from "react-toastify";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { api1 } from "../../utils/axiosInstance";
import {
  race as raceOptions,
  aiSkills as aiSkillsOptions,
  aiCareerTitles,
} from "../../constants/constants";

const UserList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [mentorId, setMentorId] = useState(null);
  const [email, setEmail] = useState({ subject: "", message: "" });

  const [filters, setFilters] = useState({
    search: "",
    subscriptionLevel: "",
    race: "",
    gender: "",
    country: "",
    educationLevel: "",
    employmentStatus: "",
    experienceLevel: "",
    aiLiteracy: "",
    aiSkills: "",
    aiCareers: "",
    moneySpentMin: "",
    moneySpentMax: "",
  });

  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api1.get(API_ENDPOINTS.GET_PROFILES);
      setData(res.data);
      setFilteredData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = data.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const emailMatch = user.email?.toLowerCase().includes(filters.search.toLowerCase());
      const nameMatch = fullName.includes(filters.search.toLowerCase());

      const userPlan = user.subscription?.plan?.toLowerCase();
      const subscriptionMatch = !filters.subscriptionLevel
        ? true
        : filters.subscriptionLevel === "free"
        ? !user.subscription?.isSubscribed || userPlan === "free"
        : user.subscription?.isSubscribed && userPlan === filters.subscriptionLevel.toLowerCase();

      return (
        (!filters.search || nameMatch || emailMatch) &&
        (!filters.gender || user.gender?.toLowerCase() === filters.gender.toLowerCase()) &&
        (!filters.race || user.race?.toLowerCase() === filters.race.toLowerCase()) &&
        (!filters.country || user.country?.toLowerCase() === filters.country.toLowerCase()) &&
        (!filters.educationLevel ||
          user.highestLevelEducation?.toLowerCase() === filters.educationLevel.toLowerCase()) &&
        (!filters.employmentStatus ||
          user.employmentStatus?.toLowerCase() === filters.employmentStatus.toLowerCase()) &&
        (!filters.experienceLevel ||
          user.yearsOfExperience?.toLowerCase() === filters.experienceLevel.toLowerCase()) &&
        (!filters.aiLiteracy ||
          user.aiReadinessQuizCompleted?.toLowerCase() === filters.aiLiteracy.toLowerCase()) &&
        (!filters.aiSkills ||
          user.technicalAISkills?.some((s) =>
            s.toLowerCase().includes(filters.aiSkills.toLowerCase())
          )) &&
        (!filters.aiCareers ||
          user.aiCareers?.some((c) =>
            c.toLowerCase().includes(filters.aiCareers.toLowerCase())
          ))
      );
    });

    setFilteredData(filtered);
  }, [filters, data]);

  const handleInputChange = (field) => (e) =>
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  const openModal = (id) => {
    setMentorId(id);
    setModalVisible(true);
  };

  const closeModal = () => {
    setEmail({ subject: "", message: "" });
    setModalVisible(false);
  };

  const sendEmail = async () => {
    const { subject, message } = email;
    if (!subject || !message) {
      toast.error("Please fill the email");
      return;
    }

    try {
      await api1.post(API_ENDPOINTS.SEND_EMAIL_TO_MENTOR, {
        mentorId,
        subject,
        message,
      });
    } catch (err) {
      console.error(err);
    } finally {
      closeModal();
    }
  };

  const formattedDate = (date) => new Date(date).toLocaleString();

  const renderSelect = (label, value, onChange, options) => (
    <select value={value} onChange={onChange} className="border px-3 py-2 rounded w-full">
      <option value="">{label}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  return (
    <Sidebar>
      <Modal isOpen={modalVisible} onClose={closeModal} width="w-[400px] md:w-[600px]">
        <div className="p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Send Email to Mentee</h2>

          <div>
            <label className="text-sm font-medium text-gray-700">Subject</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 mt-1 focus:ring-2 focus:ring-[#7E5BEF]"
              value={email.subject}
              onChange={(e) => setEmail({ ...email, subject: e.target.value })}
              placeholder="Enter email subject"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Message</label>
            <textarea
              rows={6}
              className="w-full border rounded px-3 py-2 mt-1 resize-none focus:ring-2 focus:ring-[#7E5BEF]"
              value={email.message}
              onChange={(e) => setEmail({ ...email, message: e.target.value })}
              placeholder="Write your message here..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button onClick={closeModal} className="px-4 py-2 bg-gray-200 text-gray-700 rounded">
              Cancel
            </button>
            <button onClick={sendEmail} className="px-4 py-2 bg-[#7E5BEF] text-white rounded">
              Send
            </button>
          </div>
        </div>
      </Modal>

      <div className="flex flex-col gap-6">
        <WelcomeCard title="Users" />
        {loading ? (
          <SecondaryLoader message="Loading user profiles..." />
        ) : (
          <>
            {/* Filter Section */}
            <div className="p-4 bg-white rounded shadow mb-6">
              <h2 className="text-lg font-semibold mb-4">Filter Users</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={filters.search}
                  onChange={handleInputChange("search")}
                  className="border px-3 py-2 rounded w-full"
                />
                {renderSelect(
                  "Select Subscription Level",
                  filters.subscriptionLevel,
                  handleInputChange("subscriptionLevel"),
                  [
                    { label: "Free", value: "free" },
                    { label: "Onetime", value: "onetime" },
                    { label: "Basic", value: "basic" },
                    { label: "Advance", value: "advance" },
                  ]
                )}
                {renderSelect("Select Race", filters.race, handleInputChange("race"), raceOptions)}
                {renderSelect("Gender", filters.gender, handleInputChange("gender"), [
                  { label: "Male", value: "male" },
                  { label: "Female", value: "female" },
                  { label: "Other", value: "other" },
                  { label: "Prefer Not to Say", value: "prefer_not_to_say" },
                ])}
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min spent"
                    value={filters.moneySpentMin}
                    onChange={handleInputChange("moneySpentMin")}
                    className="border px-3 py-2 rounded w-full"
                  />
                  <input
                    type="number"
                    placeholder="Max spent"
                    value={filters.moneySpentMax}
                    onChange={handleInputChange("moneySpentMax")}
                    className="border px-3 py-2 rounded w-full"
                  />
                </div>
                {renderSelect(
                  "AI Skills of Interest",
                  filters.aiSkills,
                  handleInputChange("aiSkills"),
                  aiSkillsOptions
                )}
                {renderSelect(
                  "AI Interested Careers",
                  filters.aiCareers,
                  handleInputChange("aiCareers"),
                  aiCareerTitles
                )}
              </div>
            </div>

            {/* User List Table */}
            <div className="p-6 bg-white min-h-screen">
              <h3 className="text-lg font-semibold mb-4">Users List</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="border-t border-b uppercase text-sm">
                    <tr>
                      <th className="py-3 px-6 text-left">Basic Info</th>
                      <th className="py-3 px-6 text-center">Created Date</th>
                      <th className="py-3 px-6 text-center">Current Job</th>
                      <th className="py-3 px-6 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((user, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-4 px-6">
                          <div className="font-medium">{`${user.firstName} ${user.lastName}`}</div>
                          <div className="text-sm text-gray-500">{user.country}</div>
                        </td>
                        <td className="py-4 px-6 text-center">{formattedDate(user.createdAt)}</td>
                        <td className="py-4 px-6 text-center">{user.currentJob}</td>
                        <td className="py-4 px-6 flex justify-center items-center gap-2">
                          <button
                            onClick={() => navigate(user.userId._id)}
                            className="bg-[#02B6B31F] text-[#1DB9AA] flex items-center gap-1 px-5 py-2 rounded text-sm"
                          >
                            <Eye className="w-3" /> View Profile
                          </button>
                          <button
                            onClick={() => openModal(user.userId._id)}
                            className="bg-[#7E5BEF] text-white flex items-center gap-1 px-5 py-2 rounded text-sm"
                          >
                            <Send className="w-3" /> Send Email
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default UserList;
